import { useHandler } from "@/hooks";
import { useRefInputStore } from "@/state/stores";
import { useEffect } from "react";
import { useIsNewProject } from "./useIsNewProject";

const options = [
  "a calendar sync API 🗓️",
  "a Slack office presence bot 🤖",
  "a Discord chatbot 🤖",
  "something cool 🧊",
  "an AI knowledge base API 📚",
  "a weather API 🌤️",
  "a recipe builder 🍕",
  "a travel itinerary builder 🗺️",
  "an incident response chatbot 🚨",
] as const;

export function usePlaceholderAnimation() {
  const textareaRef = useRefInputStore(({ inputRef }) => inputRef);
  const isNewProject = useIsNewProject();

  const resetPlaceholder = useHandler(() => {
    if (!textareaRef.current) {
      return;
    }
    textareaRef.current.placeholder = isNewProject
      ? "Build me a..."
      : "Ask Fiberplane...";
  });

  useEffect(() => {
    if (!isNewProject || !textareaRef.current) {
      resetPlaceholder();
      return;
    }

    const textarea = textareaRef.current;
    let currentIndex = 0;
    let typingTimeout: ReturnType<typeof setTimeout>;
    resetPlaceholder();

    const typeOption = () => {
      if (textarea.value.length > 0) {
        resetPlaceholder();
        return;
      }

      const option = options[currentIndex];
      let charIndex = 0;

      const reverseType = () => {
        if (charIndex <= 0) {
          currentIndex = (currentIndex + 1) % options.length;
          typeOption();
          return;
        }

        charIndex--;
        textarea.placeholder = `Build me ${option.slice(0, charIndex)}`;
        typingTimeout = setTimeout(reverseType, 50);
      };

      const typeCharacter = () => {
        textarea.placeholder = `Build me ${option.slice(0, charIndex + 1)}`;
        charIndex++;

        if (charIndex < option.length) {
          typingTimeout = setTimeout(typeCharacter, 30);
          return;
        }

        typingTimeout = setTimeout(reverseType, 1200);
      };

      typeCharacter();
    };

    const initialTimeout = setTimeout(typeOption, 1200);

    return () => {
      clearTimeout(typingTimeout);
      clearTimeout(initialTimeout);
    };
  }, [textareaRef, isNewProject, resetPlaceholder]);
}
